import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';

import './lib/arrayExtensions';

import AlertsRouteGate from './AlertsRouteGate';
import ClarityRouteTracker from './components/ClarityRouteTracker';
import { DebugEnvironmentPane, DebugSentryTestPane } from './components/debug';
import ErrorBoundary from './components/ErrorBoundary';
import MixpanelIdentifier from './components/MixpanelIdentifier';
import { useFeatureGate, useFeatureGates } from './hooks/useFeatureGates';
import { useFinancialAccountLiveUpdates } from './hooks/useFinancialAccountLiveUpdates';
import { useWorkItemLiveUpdates } from './hooks/useWorkItemLiveUpdates';
import {
  AccountOverviewPane,
  AccountPane,
  AssistantPane,
  BillPane,
  DashboardPane,
  MerchantPane,
  ObligationsPane,
  RecurringTransactionPane,
  RecurringTransactionsPane,
  SettingsPane,
  SpendingPane,
  TransactionPane,
} from './panes';
import { PATHS } from './paths';
import AssistantTurnStatusProvider from './providers/AssistantTurnStatusProvider';
import AuthProvider, { useAuthState } from './providers/AuthProvider';
import MarkerProvider from './providers/MarkerProvider';
import SidePanelProvider from './providers/SidePanelProvider';
import TransactionsProvider from './providers/TransactionsProvider';
import { HomeScene, LoginScene, OnboardingScene } from './scenes';

import type { FeatureGateName } from '@orn/global/featureGates/featureGates.js';

const removeLeadingSlash = (path: string): string => {
  return path.startsWith('/') ? path.slice(1) : path;
};

interface IFeatureGateRouteProps {
  /** Gate the viewer must pass to reach the route. */
  gate: FeatureGateName;
  /**
   * Route body rendered when the gate passes. Omit for a parent route
   * guarding a nested `/path/*` subtree, which renders an `<Outlet />`.
   */
  element?: React.ReactNode;
}

/**
 * Route guard that admits a viewer iff `gate` is enabled for them, and
 * redirects to the dashboard otherwise. Goes through `useFeatureGate` so the
 * same `enabledFeatureGates` set (server-side dev defaults included) that
 * drives the sidebar entries also gates the route.
 *
 * Renders `null` while the bootstrap query is loading and no cached set
 * exists yet, so a cache-cold tick can't bounce a viewer the server is about
 * to admit.
 *
 * Sidebar entries hide themselves behind the same gates, but this guard is
 * the authority for direct URL access.
 */
const FeatureGateRoute = ({ gate, element }: IFeatureGateRouteProps) => {
  const enabled = useFeatureGate(gate);
  const { flags, loading } = useFeatureGates();
  if (loading && flags.size === 0) {
    return null;
  }
  if (!enabled) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }
  return element ?? <Outlet />;
};

/**
 * At `xl+` the assistant lives as a side panel in `HomeScene` (open by
 * default), so this route would otherwise mount a *second* `AssistantPane`
 * and its live WebSocket — the server evicts the duplicate into a terminal
 * `superseded` state. We redirect to the dashboard there instead; the
 * already-mounted panel is the assistant. Below `xl` there is no room for a
 * side panel, so the route remains the assistant's home and renders the pane.
 * The redirect is keyed on the same `useBreakpoint('xl')` signal that gates
 * the panel's mount, keeping the two sites mutually exclusive.
 */
const AssistantRouteGate = () => {
  const isXl = useBreakpoint('xl');
  if (isXl) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }
  return <AssistantPane />;
};

/**
 * Renders nothing; mounts the app-wide live financial-account subscription so
 * any pane showing an account name updates in place when a writer (the
 * assistant's rename tool is the pilot) changes it.
 *
 * Split in two so the hook is never mounted for a signed-out viewer: it opens
 * a query and a socket that only a session can satisfy, and this component
 * sits outside `Routes`, so it would otherwise run on `/login`.
 */
const FinancialAccountLiveUpdatesMounted = () => {
  useFinancialAccountLiveUpdates();
  return null;
};

const FinancialAccountLiveUpdates = () => {
  const { user } = useAuthState();
  return user ? <FinancialAccountLiveUpdatesMounted /> : null;
};

/**
 * Renders nothing; mounts the app-wide live work-item subscription so alert
 * surfaces (the bell's pip, an open Alerts Center) refresh when any writer
 * raises, archives, edits, or reads the household's work items.
 *
 * Split in two for the same signed-out reason as its account sibling above.
 */
const WorkItemLiveUpdatesMounted = () => {
  useWorkItemLiveUpdates();
  return null;
};

const WorkItemLiveUpdates = () => {
  const { user } = useAuthState();
  return user ? <WorkItemLiveUpdatesMounted /> : null;
};

const MainApp = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <MarkerProvider>
            <ClarityRouteTracker />
            <FinancialAccountLiveUpdates />
            <WorkItemLiveUpdates />
            <MixpanelIdentifier />
            <TransactionsProvider>
              <SidePanelProvider>
                <AssistantTurnStatusProvider>
                  <Routes>
                    <Route path={PATHS.HOME} element={<HomeScene />}>
                      <Route path={removeLeadingSlash(PATHS.ACCOUNTS)} element={<AccountOverviewPane />} />
                      <Route path={`${removeLeadingSlash(PATHS.ACCOUNTS)}/:accountId`} element={<AccountPane />} />
                      <Route path={`${removeLeadingSlash(PATHS.MERCHANTS)}/:merchantId`} element={<MerchantPane />} />
                      <Route
                        path={removeLeadingSlash(PATHS.TRANSACTION(':transactionId'))}
                        element={<TransactionPane />}
                      />
                      <Route path={removeLeadingSlash(PATHS.BILL(':billId'))} element={<BillPane />} />
                      <Route element={<FeatureGateRoute gate={FeatureGate.DebugTools} />}>
                        <Route path={removeLeadingSlash(PATHS.DEBUG_SENTRY_TEST)} element={<DebugSentryTestPane />} />
                        <Route path={removeLeadingSlash(PATHS.DEBUG_ENVIRONMENT)} element={<DebugEnvironmentPane />} />
                      </Route>
                      <Route path={removeLeadingSlash(PATHS.DASHBOARD)} element={<DashboardPane />} />
                      <Route path={removeLeadingSlash(PATHS.ASSISTANT)} element={<AssistantRouteGate />} />
                      <Route path={removeLeadingSlash(PATHS.SPENDING())} element={<SpendingPane />} />
                      <Route
                        path={removeLeadingSlash(PATHS.RECURRING_TRANSACTIONS)}
                        element={<RecurringTransactionsPane />}
                      />
                      <Route
                        path={removeLeadingSlash(PATHS.RECURRING_TRANSACTION(':recurringTransactionId'))}
                        element={<RecurringTransactionPane />}
                      />
                      <Route
                        path={removeLeadingSlash(PATHS.OBLIGATIONS)}
                        element={<FeatureGateRoute gate={FeatureGate.Obligations} element={<ObligationsPane />} />}
                      />
                      <Route path={removeLeadingSlash(PATHS.ALERTS)} element={<AlertsRouteGate />} />
                      <Route path={removeLeadingSlash(PATHS.SETTINGS)} element={<SettingsPane />} />
                      <Route index element={<Navigate to={PATHS.DASHBOARD} replace />} />
                    </Route>

                    <Route path={PATHS.LOGIN} element={<LoginScene />} />
                    <Route path={PATHS.ONBOARDING} element={<OnboardingScene />} />
                    <Route path="*" element={<Navigate to={PATHS.DASHBOARD} replace />} />
                  </Routes>
                </AssistantTurnStatusProvider>
              </SidePanelProvider>
            </TransactionsProvider>
          </MarkerProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default MainApp;
